import { createContext, DependencyList, ReactNode, useCallback, useContext, useEffect, useId, useMemo, useState } from "react"
import { Hit } from "../Entity.ts"
import { mat4 } from "../VectorMath.ts"

export type DrawProps = {
  gl: WebGL2RenderingContext
  clipFromWorld: mat4

  /** Location of mouse hover. */
  mouseHit: Hit
}

export type DrawCall = (props: DrawProps) => void

/**
 * Send a `DrawCall` up to the nearest `GatherDrawCalls` ancestor.
 * @param name A string for debugging.
 * @param drawCall A function that draws to a GL.
 * @param deps The dependencies of `drawCall`.
 * @param options.priority The priority of this draw call. A higher priority is drawn later, and so can overwrite the output of earlier calls.
 */
export function useDrawCall(_name: string, drawCall: DrawCall | null, deps: DependencyList, options?: { priority: number }) {
  const id = useId()
  const yeet = useContext(YeetContext)

  const memoCall = useCallback(drawCall ?? (() => { }), deps)

  const priority = options?.priority ?? 0
  const isNull = drawCall === null

  useEffect(() => {
    if (isNull) {
      yeet(id, null)
      return
    }

    const record: DrawCallRecord = {
      call: memoCall,
      priority: priority
    }
    yeet(id, record)
    return () => {
      yeet(id, null)
    }
  }, deps.concat([yeet, isNull, memoCall, priority]))
}

/**
 * @returns The draw calls gathered by the nearest `GatherDrawCalls` ancestor.
 */
export function useDrawCalls(): DrawCall[] {
  const map = useContext(GatherContext)
  const answer: DrawCall[] = useMemo(() => {
    return Array.from(map.values())
      .sort((a, b) => a.priority - b.priority)
      .map(record => record.call)
  }, [map])
  return answer
}

/**
 * Collect the `DrawCall`s passed to `useDrawCall` in my descendants, and make them available to my descendants through `useDrawCalls`.
 */
export function GatherDrawCalls(props: {
  children: ReactNode
}) {
  const [drawCallMap, setDrawCallMap] = useState<DrawCallMap>(new Map())

  const yeet: Yeet = useCallback((id, drawCall) => {
    setDrawCallMap(entities => {
      if (drawCall && entities.get(id) !== drawCall) {
        return new Map(entities).set(id, drawCall)
      } else if (drawCall === null && entities.has(id)) {
        const copy = new Map(entities)
        copy.delete(id)
        return copy
      } else {
        return entities
      }
    })
  }, [setDrawCallMap])

  return <YeetContext.Provider value={yeet}>
    <GatherContext.Provider value={drawCallMap}>
      {props.children}
    </GatherContext.Provider>
  </YeetContext.Provider>
}

interface DrawCallRecord {
  call: DrawCall
  priority: number
}

type Yeet = (id: string, record: DrawCallRecord | null) => void
const YeetContext = createContext<Yeet>((_id) => { })

type DrawCallMap = Map<string, DrawCallRecord>
const GatherContext = createContext<DrawCallMap>(new Map())
